import { useLocation } from "react-router-dom";

import { clientName } from "../api/types";
import type { Client } from "../api/types";
import { useGoBack } from "../lib/back";
import Breadcrumb from "./Breadcrumb";
import "./TopBar.css";

/**
 * Screen context on the left, page actions on the right.
 *
 * The client dropdown and the Client Setup button are gone. They were a
 * second way to do what the sidebar already does, and Setup acted on
 * whichever client the dropdown pointed at, so you had to read two places to
 * know what it would do. Setup lives on the client it acts on now.
 *
 * Theme and account stay in the sidebar footer. The bell stays here because
 * project updates belong to the current screen more than to the signed-in
 * person, and keeping it beside search avoids another floating one-off
 * control elsewhere in the shell.
 */
export default function TopBar({
  clients,
  currentId,
  variant,
}: {
  // Only for the breadcrumb now. Picking a client is the sidebar's job.
  clients: Client[];
  currentId: string | null;
  variant: "dashboard" | "default";
}) {
  const goBack = useGoBack();
  const { pathname } = useLocation();
  const routeClientId = pathname.match(/^\/clients\/([^/]+)(?:\/|$)/)?.[1] ?? null;
  const current =
    clients.find((c) => c.id === routeClientId) ??
    clients.find((c) => c.id === currentId) ??
    clients[0] ??
    null;

  const crumbItems = (() => {
    if (pathname === "/roles" || /^\/roles\/[^/]+$/.test(pathname)) {
      return [{ label: "Roles" }];
    }
    if (pathname === "/clients/new") {
      return [{ label: "Client", to: "/" }, { label: "Add Client" }];
    }
    if (/^\/clients\/[^/]+\/edit$/.test(pathname)) {
      return [
        { label: "Client", to: "/" },
        current ? { label: clientName(current), to: `/clients/${current.id}` } : { label: "Client" },
        { label: "Edit Client" },
      ];
    }
    if (/^\/clients\/[^/]+\/calls\/new$/.test(pathname)) {
      return [
        { label: "Client", to: "/" },
        current ? { label: clientName(current), to: `/clients/${current.id}` } : { label: "Client" },
        { label: "Record a Call" },
      ];
    }
    if (/^\/clients\/[^/]+$/.test(pathname)) {
      return [
        { label: "Client", to: "/" },
        { label: current ? clientName(current) : "All clients" },
      ];
    }
    return [{ label: "Client" }, { label: current ? clientName(current) : "All clients" }];
  })();
  return (
    <>
      {/* The dashboard used to carry a Current Client dropdown and a Client
          Setup button here. Both were a second way to do what the sidebar
          already does, and 1v1 called them redundant: the sidebar lists every
          client, shows which one is current, and now carries setup on the
          client it applies to. */}
      {variant !== "dashboard" && (
        <>
          {/* Figma: back button at x=275 then the breadcrumb, with the search
              field pushed over to the right. The dashboard is the only screen
              that puts the search on the left, because it has no breadcrumb. */}
          <button
            className="backbtn"
            type="button"
            onClick={goBack}
            aria-label="Go back"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M10 3.5 5.5 8l4.5 4.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <Breadcrumb items={crumbItems} />
        </>
      )}
    </>
  );
}
